/* Combined Teacher/Staff form dialog — extracted from Staff.js (iter84).
 * `kind` = 'teacher' | 'staff' controls fields and test IDs.
 * Preserves all existing data-testids. */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Camera, X as XIcon } from '@phosphor-icons/react';
import { DatePicker } from '../ui/date-picker';
import StaffBenefitsSection from './StaffBenefitsSection';

/**
 * @param {Object} props
 * @param {'teacher'|'staff'} props.kind
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {function} props.onSubmit
 * @param {Object} props.form
 * @param {function} props.setForm
 * @param {Object} props.selectedItem  Existing record (null = create mode)
 * @param {function} props.handlePhotoUpload
 * @param {Array<{value:string,key:string}>} props.contractTypes
 * @param {string} props.language
 * @param {function} props.t
 */
export const StaffFormDialog = ({
  kind, open, onOpenChange, onSubmit, form, setForm, selectedItem,
  handlePhotoUpload, contractTypes, language, t,
}) => {
  const isTeacher = kind === 'teacher';
  const prefix = isTeacher ? 'teacher' : 'staff';
  const title = selectedItem
    ? (isTeacher
        ? (language === 'fr' ? 'Modifier enseignant' : 'Edit Teacher')
        : (language === 'fr' ? 'Modifier' : 'Edit'))
    : (isTeacher ? t('addTeacher') : t('addStaff'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${prefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Photo section */}
          <div className="flex items-center gap-4 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
            <div className="relative">
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="Avatar"
                  className="h-20 w-20 rounded-full object-cover border-2 border-[#E2E8F0]"
                  data-testid={`${prefix}-photo-preview`} />
              ) : (
                <div className="h-20 w-20 rounded-full bg-[#E2E8F0] flex items-center justify-center text-[#94A3B8]">
                  <Camera size={28} />
                </div>
              )}
              {form.photo_base64 && (
                <button type="button"
                  onClick={() => setForm({ ...form, photo_base64: '' })}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-600"
                  title={language === 'fr' ? 'Retirer la photo' : 'Remove photo'}
                  data-testid={`${prefix}-photo-remove`}>
                  <XIcon size={12} weight="bold" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <Label className="font-manrope text-xs">
                {language === 'fr' ? 'Photo de l\'employé' : 'Employee photo'}
              </Label>
              <Input type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0], setForm, form)}
                className="border-[#E2E8F0] text-xs mt-1 cursor-pointer"
                data-testid={`${prefix}-photo-input`} />
              <p className="text-[10px] text-[#94A3B8] mt-1">
                {language === 'fr' ? 'JPG/PNG/WebP, redimensionnée à 320 px max.' : 'JPG/PNG/WebP, resized to 320 px max.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('firstName')}</Label>
              <Input value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required className="border-[#E2E8F0]" />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('lastName')}</Label>
              <Input value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required className="border-[#E2E8F0]" />
            </div>
          </div>

          {/* Teacher-only: subject */}
          {isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('subject')}</Label>
                <Input value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="border-[#E2E8F0]" />
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{t('function')}</Label>
                <Input value={form.function}
                  onChange={(e) => setForm({ ...form, function: e.target.value })}
                  className="border-[#E2E8F0]"
                  placeholder={language === 'fr' ? 'Ex: Professeur principal' : 'e.g. Lead Teacher'}
                  data-testid="teacher-function" />
              </div>
            </div>
          )}

          {/* Staff-only: function + contract in one row */}
          {!isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('function')}</Label>
                <Input value={form.function}
                  onChange={(e) => setForm({ ...form, function: e.target.value })}
                  className="border-[#E2E8F0]"
                  placeholder={language === 'fr' ? 'Ex: Chef cuisinier' : 'e.g. Head Chef'}
                  data-testid="staff-function" />
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{t('contractType')}</Label>
                <Select value={form.contract_type || undefined}
                  onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="staff-contract-type">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {contractTypes.map(c => (
                      <SelectItem key={c.value} value={c.value}>{t(c.key)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Teacher: contract + salary */}
          {isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('contractType')}</Label>
                <Select value={form.contract_type || undefined}
                  onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="teacher-contract-type">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {contractTypes.map(c => (
                      <SelectItem key={c.value} value={c.value}>{t(c.key)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{t('salary')} (FCFA)</Label>
                <Input type="number" value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  className="border-[#E2E8F0]" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('contact')}</Label>
              <Input value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className="border-[#E2E8F0]"
                data-testid={isTeacher ? undefined : 'staff-contact'} />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{isTeacher ? t('email') : 'Email'}</Label>
              <Input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border-[#E2E8F0]"
                placeholder={isTeacher ? undefined : 'prenom@example.com'}
                data-testid={isTeacher ? undefined : 'staff-email'} />
            </div>
          </div>

          {/* Staff: salary + birthdate */}
          {!isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('salary')} (FCFA)</Label>
                <Input type="number" value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  className="border-[#E2E8F0]" />
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{t('birthdate')}</Label>
                <DatePicker value={form.birthdate}
                  onChange={(v) => setForm({ ...form, birthdate: v })}
                  testid="staff-birthdate" className="w-full" />
              </div>
            </div>
          )}

          {/* Teacher: birthdate + hire_date */}
          {isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('birthdate')}</Label>
                <DatePicker value={form.birthdate}
                  onChange={(v) => setForm({ ...form, birthdate: v })}
                  testid="teacher-birthdate" className="w-full" />
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{t('hireDate')}</Label>
                <DatePicker value={form.hire_date}
                  onChange={(v) => setForm({ ...form, hire_date: v })}
                  testid="teacher-hire-date" className="w-full" />
              </div>
            </div>
          )}

          {/* Staff: hire_date alone */}
          {!isTeacher && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-manrope">{t('hireDate')}</Label>
                <DatePicker value={form.hire_date}
                  onChange={(v) => setForm({ ...form, hire_date: v })}
                  testid="staff-hire-date" className="w-full" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-manrope">{t('address')}</Label>
            <Textarea value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="border-[#E2E8F0]" rows={2}
              data-testid={`${prefix}-address`} />
          </div>

          {/* iter73 — Prime de transport & Avantages */}
          <StaffBenefitsSection form={form} setForm={setForm} kind={kind} />

          {/* iter56 — Bank details */}
          <div className="p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] space-y-3">
            <p className="text-xs font-semibold text-[#475569]">
              🏦 Coordonnées bancaires (virement paie)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="font-manrope text-xs">IBAN / Numéro compte</Label>
                <Input value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                  className="border-[#E2E8F0] font-mono text-xs"
                  placeholder="CI93 CI00 8010 …"
                  data-testid={`${prefix}-iban`} />
              </div>
              <div className="space-y-1">
                <Label className="font-manrope text-xs">BIC / SWIFT</Label>
                <Input value={form.bic}
                  onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })}
                  className="border-[#E2E8F0] font-mono text-xs"
                  placeholder="ECOCIABI"
                  data-testid={`${prefix}-bic`} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="font-manrope text-xs">Nom de la banque</Label>
              <Input value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                className="border-[#E2E8F0] text-xs"
                placeholder="Ecobank, Société Générale…"
                data-testid={`${prefix}-bank-name`} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline"
              onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
            <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]"
              data-testid={isTeacher ? undefined : 'staff-submit'}>
              {t('save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
